import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetClientAdminsClient } from '~/hooks';
import { BILLING_ADDRESS, ERROR_MESSAGE, STRIPE_SOURCE_PARAM, SUCCESS_MESSAGE } from '../constants';
import type { ContractInfoPresentation } from '../presentations';
import { useGetStripeCreditCard } from './useGetStripeCreditCard';
import { usePostClientAdminsCardRegistrations } from './usePostClientAdminsCardRegistrations';

type PresentationProps = React.ComponentProps<typeof ContractInfoPresentation>;

type IUseContractInfo = () => {
  state: {
    /** 導入企業情報 */
    data: ReturnType<typeof useGetClientAdminsClient>['data'];
    /** 既定カード情報の取得結果（登録済み / 未登録 / 取得失敗） */
    creditCardResult: ReturnType<typeof useGetStripeCreditCard>['data'];
    /** URL の `stripe_source` クエリの値。付いていない場合は null（遷移元の判定は converter で行う） */
    stripeSource: string | null;
    /** カード変更フォームを展開しているか */
    isFormExpanded: PresentationProps['isFormExpanded'];
    /** SetupIntent 発行 / カード登録に失敗したときのエラー文言 */
    errorMessage: PresentationProps['errorMessage'];
    /** カード登録成功時の案内文言 */
    successMessage: PresentationProps['successMessage'];
    /** 保存ボタンを非活性にするか */
    isSubmitDisabled: PresentationProps['isSubmitDisabled'];
    /** カード入力フォームを作り直すためのキー（カード登録の失敗ごとに進む） */
    paymentFieldResetKey: PresentationProps['paymentFieldResetKey'];
  };
  actions: {
    handleToggleForm: PresentationProps['onToggleForm'];
    handleSubmitCard: PresentationProps['onSubmitCard'];
  };
};

/**
 * ご契約情報のコンポーネントフック
 *
 * - threeLetterCode を Cookie から取得し、各 API hook に渡す
 * - 導入企業情報（会社情報 / プラン情報 / ご担当者情報 / ご請求先情報の表示元）を取得する
 * - お支払い方法セクションのカード情報を取得し、カード変更フォームの開閉と登録を扱う
 *   - 編集ボタン押下でカード変更フォームを展開し、SetupIntent（client_secret）を発行する
 *   - 保存ボタン押下で Payment Element の入力内容を Stripe に送信し、カードを登録する
 */
export const useContractInfo: IUseContractInfo = () => {
  const threeLetterCode = getThreeLetterCode() ?? '';

  const { data } = useGetClientAdminsClient({ threeLetterCode });
  const { data: creditCardResult, mutate } = useGetStripeCreditCard({ threeLetterCode });

  const [searchParams] = useSearchParams();
  const stripeSource = searchParams.get(STRIPE_SOURCE_PARAM.key);

  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<PresentationProps['errorMessage']>(undefined);
  const [successMessage, setSuccessMessage] =
    useState<PresentationProps['successMessage']>(undefined);
  /** confirmSetup 実行中か（二重送信を防ぐため保存ボタンを非活性にする） */
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * 発行済み SetupIntent の client_secret。
   *
   * フォームを格納してもクリアせず保持し、再展開時の再発行を避ける。
   * SetupIntent は決済失敗後も `requires_payment_method` に戻って再利用できるため、
   * 開閉のたびに発行すると使われないまま放置される孤児 SetupIntent が Stripe 上に溜まる。
   *
   * confirmSetup に渡すだけでなく保存ボタンの非活性判定にも使う（= レンダリング中に読む）ため、
   * ref ではなく state で保持する。
   */
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);

  /**
   * カード入力フォーム（Payment Element）を作り直すための世代カウンタ。
   *
   * カード登録が失敗したら値を進め、Presentation 側で `key` として使って Payment Element を
   * 再マウントする。Stripe の Elements インスタンスは confirmSetup の試行状態を内部に持つため、
   * 失敗したインスタンスを使い回すと正しいカードを入力し直しても同じエラーが返り、
   * リロードするまで復帰できなくなる。
   */
  const [paymentFieldResetKey, setPaymentFieldResetKey] = useState(0);

  const { trigger, isMutating } = usePostClientAdminsCardRegistrations(threeLetterCode, {
    onSuccess: responseData => {
      setClientSecret(responseData.card_registration.client_secret);
    },
    onError: error => {
      // 発行に失敗した client_secret は保持しない（次回の展開で再取得させる）
      setClientSecret(undefined);
      setErrorMessage(
        error.response?.status === 404 ? ERROR_MESSAGE.notCardPayment : ERROR_MESSAGE.unexpected
      );
    },
  });

  /**
   * カード変更フォームの展開 / 格納を切り替える
   *
   * 展開時は client_secret が未取得の場合のみ SetupIntent を発行する。
   * 格納時はエラー文言をクリアし、client_secret は再利用のため保持する。
   */
  const handleToggleForm: PresentationProps['onToggleForm'] = () => {
    if (isFormExpanded) {
      setIsFormExpanded(false);
      setErrorMessage(undefined);
      return;
    }

    setIsFormExpanded(true);
    setSuccessMessage(undefined);
    if (!clientSecret) {
      trigger();
    }
  };

  /**
   * Payment Element の入力内容を Stripe に送信し、カードを登録する
   *
   * deferred モード（Elements に clientSecret を渡さない）では、confirmSetup の前に
   * elements.submit() で入力値のバリデーション・収集を行う必要がある。
   *
   * 成功時はフォームを閉じ、反映ラグの案内を表示する（新カードの既定化は
   * setup_intent.succeeded Webhook が非同期で行うため、画面のカード情報は即時更新されない）。
   */
  const handleSubmitCard: PresentationProps['onSubmitCard'] = async (stripe, elements) => {
    // 非活性化しているため通常は到達しないが、型を絞るために早期リターンする
    if (!clientSecret) return;

    setIsSubmitting(true);
    setErrorMessage(undefined);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message ?? ERROR_MESSAGE.cardRegistrationFailed);
      setIsSubmitting(false);
      return;
    }

    const { error: confirmError } = await stripe.confirmSetup({
      elements,
      clientSecret,
      confirmParams: {
        payment_method_data: {
          // fields で 'never' にしたフィールドは自前で渡す義務がある（詳細は BILLING_ADDRESS の JSDoc）
          billing_details: { address: BILLING_ADDRESS },
        },
      },
      // カード決済のみのためリダイレクトは発生しないが、'always' だと return_url が必須になる
      redirect: 'if_required',
    });
    setIsSubmitting(false);

    if (confirmError) {
      setErrorMessage(confirmError.message ?? ERROR_MESSAGE.cardRegistrationFailed);
      // 失敗した SetupIntent と Payment Element は使い回さず、次の試行のために作り直す。
      // 同じ client_secret・同じ Elements インスタンスのまま再送すると、正しいカードを
      // 入力し直しても同じエラーが返り、リロードしないと復帰できなくなる。
      // 再発行が失敗した場合は onError がエラー文言を上書きする（発行できないことのほうが
      // ユーザーの次の行動に関わるため、Stripe のエラー文言より優先する）。
      setClientSecret(undefined);
      setPaymentFieldResetKey(key => key + 1);
      trigger();
      return;
    }

    setIsFormExpanded(false);
    setSuccessMessage(SUCCESS_MESSAGE);
    // Webhook（setup_intent.succeeded）の反映ラグにより旧カード / 404 が返る可能性があるが、
    // 再取得は 1 回のみでよい（ポーリングはしない）。ラグの案内は SUCCESS_MESSAGE 側で行う。
    mutate();
  };

  return {
    state: {
      data,
      creditCardResult,
      stripeSource,
      isFormExpanded,
      errorMessage,
      successMessage,
      // client_secret 未取得（発行中含む）では confirmSetup を実行できない
      isSubmitDisabled: !clientSecret || isMutating || isSubmitting,
      paymentFieldResetKey,
    },
    actions: { handleToggleForm, handleSubmitCard },
  };
};
